import React, { useMemo, useState } from 'react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import PreviewListToolbar from './PreviewListToolbar'

export default function GamingVenueMappingForm({
  venueCheck,
  venueMappings,
  onChange,
}) {
  const [search, setSearch] = useState('')
  const [pageSize, setPageSize] = useState(15)
  const [offset, setOffset] = useState(0)

  const unmatched = venueCheck?.unmatched_file_venues || []
  const venues = venueCheck?.configured_venues || []
  const needle = search.trim().toLowerCase()

  const filtered = useMemo(() => {
    if (!needle) return unmatched
    return unmatched.filter((name) => String(name).toLowerCase().includes(needle))
  }, [unmatched, needle])

  if (!unmatched.length) return null

  const page = filtered.slice(offset, offset + pageSize)
  const hasMore = offset + pageSize < filtered.length
  const showToolbar = unmatched.length > 6 || !!needle

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950 space-y-3">
      <div className="flex gap-2 items-start">
        <ExclamationTriangleIcon className="w-5 h-5 shrink-0 text-amber-600" />
        <div>
          <p className="font-medium">Map extract site names to venues</p>
          <p className="mt-1 text-xs text-amber-900">
            Site name(s) in this file do not match any configured <strong>Gaming extract name</strong>.
            Choose which venue each label should import as — rows will be stored under that venue&apos;s extract name.
          </p>
        </div>
      </div>

      {showToolbar && (
        <PreviewListToolbar
          search={search}
          onSearchChange={(v) => { setSearch(v); setOffset(0) }}
          searchPlaceholder="Filter unmatched site names…"
          pageSize={pageSize}
          onPageSizeChange={(n) => { setPageSize(n); setOffset(0) }}
          offset={offset}
          pageCount={page.length}
          totalMatched={filtered.length}
          hasMore={hasMore}
          onPrev={() => setOffset((o) => Math.max(0, o - pageSize))}
          onNext={() => setOffset((o) => o + pageSize)}
        />
      )}

      <div className="space-y-2">
        {page.map((fileName) => (
          <label key={fileName} className="block">
            <span className="text-xs font-medium text-gray-700">
              <span className="font-mono">{fileName}</span>
              {' → '}
            </span>
            <select
              className="mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
              value={venueMappings[fileName] || ''}
              onChange={(e) => onChange(fileName, e.target.value)}
            >
              <option value="">Select venue…</option>
              {venues.map((v) => (
                <option key={v.venue_id} value={v.venue_id}>
                  {v.name}
                  {v.gaming_extract_venue_name ? ` (extract: ${v.gaming_extract_venue_name})` : ''}
                </option>
              ))}
            </select>
          </label>
        ))}
        {!page.length && (
          <p className="text-xs text-gray-600">No unmatched site names match this filter.</p>
        )}
      </div>

      {venueCheck?.file_venue_names?.some((n) => !unmatched.includes(n)) && (
        <p className="text-xs text-gray-600">
          Other site names in the file already match configured venues and do not need mapping.
        </p>
      )}
    </div>
  )
}
